import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Flag } from "lucide-react";
import { toast } from "sonner";
import { lpApi, lpErr, card, h2, input, btn } from "@/components/portal/lp/lpApi";

/** Open 30/60-Day Stabilization Roadmaps for the signed-in resident (works with PIN sign-in and links alike). */
export function useRoadmapTasks(token) {
  const [tasks, setTasks] = useState(null);
  const reload = () => lpApi(token).get("/portal/resident/roadmap-tasks").then((d) => setTasks(d.tasks)).catch(() => setTasks([]));
  useEffect(() => { reload(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  return [tasks, setTasks, reload];
}

function Question({ q, stage, token, onSaved }) {
  const [text, setText] = useState(q.accepted ? "" : q.answer);
  const [busy, setBusy] = useState(false);
  const [fb, setFb] = useState(q.feedback);
  const n = text.trim().length;
  const save = async () => {
    setBusy(true);
    try {
      const d = await lpApi(token).post(`/portal/resident/roadmap-tasks/${stage}/answer`, { index: q.index, text });
      setFb(d.feedback);
      d.accepted ? toast.success(d.completed ? "Roadmap complete. Great work!" : "Answer saved") : toast.message("Add a little more, then send it again");
      onSaved(d.tasks);
    } catch (e) { toast.error(lpErr(e)); }
    setBusy(false);
  };
  if (q.accepted) return (
    <div data-testid={`rm-q-done-${stage}-${q.index}`} className="text-sm border-b border-white/5 pb-2">
      <div className="flex items-center gap-1.5 text-emerald-300"><CheckCircle2 className="h-4 w-4" />{q.label}</div>
      <p className="text-white/50 text-xs mt-1 line-clamp-2">{q.answer}</p>
    </div>
  );
  return (
    <div data-testid={`rm-q-${stage}-${q.index}`} className="space-y-2 border-b border-white/5 pb-3">
      <div className="text-xs uppercase tracking-[0.2em] text-[#d4a017]">{q.label}</div>
      <p className="font-medium">{q.prompt}</p>
      {q.help && <p className="text-xs text-white/50">{q.help}</p>}
      <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} className={input} data-testid={`rm-q-input-${stage}-${q.index}`} placeholder="Write your answer…" />
      {fb && <p data-testid={`rm-q-feedback-${stage}-${q.index}`} className="text-xs text-amber-200">{fb}</p>}
      <div className="flex items-center gap-3">
        <span className={`text-xs ${n >= q.min_chars ? "text-emerald-300" : "text-white/50"}`}>{n}/{q.min_chars}</span>
        <button type="button" onClick={save} disabled={busy || n < q.min_chars} className={`${btn} ml-auto`} data-testid={`rm-q-submit-${stage}-${q.index}`}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? "Checking…" : "Send answer"}
        </button>
      </div>
    </div>
  );
}

export default function RoadmapTasks({ token, tasks, setTasks }) {
  if (!tasks?.length) return null;
  return tasks.map((t) => (
    <section key={t.stage} className={`${card} space-y-3`} data-testid={`rm-task-${t.stage}`}>
      <h2 className={h2}>{t.title} · {t.answered}/{t.of} done</h2>
      {t.questions.map((q) => <Question key={`${q.index}-${q.accepted}`} q={q} stage={t.stage} token={token} onSaved={setTasks} />)}
    </section>
  ));
}

export function RoadmapBanner({ tasks, onOpen }) {
  const t = tasks?.[0];
  if (!t) return null;
  return (
    <button type="button" onClick={onOpen} data-testid="home-roadmap-banner" className="w-full flex items-center gap-3 rounded-xl border border-[#d4a017]/60 bg-[#d4a017]/10 p-4 text-left">
      <Flag className="h-6 w-6 text-[#d4a017]" /><span><span className="block font-medium">Finish your {t.title}</span><span className="text-xs text-white/60">{t.answered} of {t.of} answered · tap to continue in Homework</span></span>
    </button>
  );
}
